import React from "react";
import { Box, Slider, Typography } from "@mui/material";

/** MUI `marks={true}` does not set `label` on each mark, so no digits render — explicit labels show 0…10. */
const RATING_SCALE_MARKS = Array.from({ length: 11 }, (_, i) => ({
  value: i,
  label: String(i),
}));

/**
 * VAS / 0–10 rating strip: gradient rail (green → red by positiveEnd), thumb, end labels + emojis (smile always on positive side).
 */
export default function RatingScaleSlider({
  value = 5,
  onChange,
  minLabel = "",
  maxLabel = "",
  positiveEnd = "min",
  disabled = false,
  colors,
}) {
  const v = Math.max(0, Math.min(10, Number(value) || 0));
  const grad =
    positiveEnd === "min"
      ? "linear-gradient(90deg, #2e7d32 0%, #c62828 100%)"
      : "linear-gradient(90deg, #c62828 0%, #2e7d32 100%)";

  const smileLeft = positiveEnd === "min";
  const smileRight = positiveEnd === "max";

  return (
    <Box sx={{ width: "100%", maxWidth: 720, py: 1 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 1,
          mb: 1,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Typography
            component="span"
            sx={{ fontSize: "1.35rem", lineHeight: 1 }}
            aria-hidden
          >
            {smileLeft ? "😊" : "😟"}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: colors?.grey?.[100] ?? "inherit",
              fontWeight: 600,
              wordBreak: "break-word",
            }}
          >
            {minLabel || "0"}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            justifyContent: "flex-end",
            textAlign: "right",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: colors?.grey?.[100] ?? "inherit",
              fontWeight: 600,
              wordBreak: "break-word",
            }}
          >
            {maxLabel || "10"}
          </Typography>
          <Typography
            component="span"
            sx={{ fontSize: "1.35rem", lineHeight: 1 }}
            aria-hidden
          >
            {smileRight ? "😊" : "😟"}
          </Typography>
        </Box>
      </Box>

      <Slider
        value={v}
        min={0}
        max={10}
        step={1}
        marks={RATING_SCALE_MARKS}
        disabled={disabled}
        onChange={(_, n) => onChange && onChange(n)}
        sx={{
          mt: 0.5,
          mb: 0.5,
          height: 10,
          "& .MuiSlider-rail": {
            opacity: 1,
            height: 10,
            borderRadius: 2,
            background: grad,
          },
          "& .MuiSlider-track": {
            display: "none",
          },
          "& .MuiSlider-thumb": {
            width: 22,
            height: 22,
            backgroundColor: colors?.grey?.[100] ?? "#fff",
            border: `2px solid ${colors?.greenAccent?.[500] ?? "#4cceac"}`,
            "&:hover, &.Mui-focusVisible": {
              boxShadow: `0 0 0 6px rgba(76, 206, 172, 0.25)`,
            },
          },
          "& .MuiSlider-mark": {
            backgroundColor: "rgba(255,255,255,0.35)",
            height: 6,
          },
          "& .MuiSlider-markLabel": {
            color: colors?.grey?.[300] ?? "inherit",
            fontSize: { xs: "0.6rem", sm: "0.7rem" },
            fontWeight: 500,
            lineHeight: 1,
            whiteSpace: "nowrap",
          },
        }}
      />
    </Box>
  );
}
